import {
  forgotPassword,
  login,
  getDetails,
  registerAffiliate,
  resetPasswordOtp,
  setNewPassword
} from 'api'
import {
  getResetEmail,
  getResetToken,
  removeResetData,
  removeSessionData,
  saveResetEmail,
  saveResetToken,
  saveSessionToken,
  saveSessionUserData
} from 'api/session'
import { LOCATION_CHANGE, LocationChangeAction, push } from 'react-router-redux'
import { setNotification } from 'redux/application/actions'
import {
  loginForm,
  newPasswordForm,
  passwordResetForm,
  signupForm,
  verifyOtpForm
} from 'redux/forms'
import { destroy, startSubmit, stopSubmit } from 'redux-form'
import {
  all,
  call,
  fork,
  put,
  select,
  take,
  takeLatest
} from 'redux-saga/effects'
import { setAuthToken } from './actions'
import * as I from './interfaces'
import { getUser } from './selectors'
import t from './types'

const COMMISSIONS_PAGE_SIZE = 20
const DEFAULT_COMMISSION_SORT: CommissionSort = 'created_at'

// The API returns a flat, paginated list; the dashboard groups commissions by the
// date they're sorted by
const toUser = (
  { data, item_count, extra }: IAffiliateDetailsResponse,
  offset: number,
  limit: number,
  sortBy: CommissionSort
): IUser => ({
  data: data.reduce((grouped: IUserData, commission) => {
    const date =
      sortBy === 'created_at' ? commission.createdAt : commission.maturityDate
    const day = date.slice(0, 10)
    return { ...grouped, [day]: [...(grouped[day] || []), commission] }
  }, {}),
  meta: { limit, offset, total: item_count, sortBy },
  extra
})

function* getDetailsSaga() {
  try {
    const { data } = yield call(getDetails, {
      offset: 0,
      limit: COMMISSIONS_PAGE_SIZE,
      sortBy: DEFAULT_COMMISSION_SORT
    })
    const user = toUser(data, 0, COMMISSIONS_PAGE_SIZE, DEFAULT_COMMISSION_SORT)

    saveSessionUserData(user)
    yield put({ type: t.SET_USER_PROFILE, user })
    yield put({ type: t.LOGIN_SUCCESS })
    return true
  } catch (error) {
    yield put({ type: t.GET_AUTH_USER_ERROR, error })
    return error
  }
}

// Passing sortBy switches the sort and reloads from the first page
function* getCommisionSaga({ paginate, sortBy }: I.IGetCommisionRequest) {
  try {
    const { meta }: IUser = yield select(getUser)
    const sort = sortBy || meta.sortBy || DEFAULT_COMMISSION_SORT

    const nextOffset = paginate ? meta.offset + meta.limit : 0
    const { data } = yield call(getDetails, {
      offset: nextOffset,
      limit: COMMISSIONS_PAGE_SIZE,
      sortBy: sort
    })
    const user = toUser(data, nextOffset, COMMISSIONS_PAGE_SIZE, sort)

    yield put({
      type: t.GET_COMMISSION_SUCCESS,
      paginate,
      user
    })
  } catch (error) {
    yield put({ type: t.GET_COMMISSION_ERROR, error })
  }
}

function* loginSaga({ email, password }: I.ILoginRequest) {
  try {
    yield put(startSubmit(loginForm.name))
    const {
      data: { token }
    } = yield call(login, email, password)
    yield put(setAuthToken(token))

    saveSessionToken({ token })
    const loaded = yield call(getDetailsSaga)

    // getDetailsSaga reports its own error, so only drop the half-created session here
    if (loaded !== true) {
      removeSessionData()
    }
    yield put(stopSubmit(loginForm.name))
  } catch (error) {
    yield put({ type: t.LOGIN_ERROR, error })
    yield put(stopSubmit(loginForm.name))
  }
}

// Sign in through the affiliate login (no new-device check), then request
// affiliate status with that session.
function* signupSaga({ email, password }: I.ISignupRequest) {
  try {
    yield put(startSubmit(signupForm.name))
    const {
      data: { token }
    } = yield call(login, email, password)
    saveSessionToken({ token })

    const {
      data: { is_approved, message }
    } = yield call(registerAffiliate)

    if (!is_approved) {
      // eslint-disable-next-line
      throw { message }
    }

    yield put(setAuthToken(token))
    const loaded = yield call(getDetailsSaga)

    // getDetailsSaga reports its own error, so only drop the half-created session here
    if (loaded !== true) {
      removeSessionData()
      yield put(stopSubmit(signupForm.name))
      return
    }

    yield put({ type: t.SIGNUP_SUCCESS })
    yield put(stopSubmit(signupForm.name))
    yield put(setNotification(message))
  } catch (error) {
    removeSessionData()
    yield put({ type: t.SIGNUP_ERROR, error })
    yield put(stopSubmit(signupForm.name))
  }
}

function* passwordResetSaga({ email }: I.IResetPasswordRequest) {
  try {
    yield put(startSubmit(passwordResetForm.name))
    yield call(forgotPassword, email)
    saveResetEmail(email)
    yield put(stopSubmit(passwordResetForm.name))
    yield put({ type: t.PASSWORD_RESET_SUCCESS })
    yield put(push('/reset_password/otp'))
  } catch (error) {
    yield put(stopSubmit(passwordResetForm.name))
    yield put({
      type: t.PASSWORD_RESET_ERROR,
      error
    })
  }
}

function* verifyOtpdSaga({ otp }: I.IVerifyOtpRequest) {
  try {
    yield put(startSubmit(verifyOtpForm.name))
    const email = getResetEmail()

    if (!email) {
      yield put(push('/reset_password'))
      // eslint-disable-next-line
      throw { message: 'Please enter your email address again' }
    }

    const {
      data: { is_valid, token }
    } = yield call(resetPasswordOtp, email, otp)

    if (!is_valid) {
      // eslint-disable-next-line
      throw { message: 'Invalid OTP' }
    }

    saveResetToken(token)
    yield put({ type: t.VERIFY_OTP_SUCCESS })
    yield put(stopSubmit(verifyOtpForm.name))
    yield put(push('/reset_password/new'))
  } catch (error) {
    yield put(stopSubmit(verifyOtpForm.name))
    yield put({
      type: t.VERIFY_OTP_ERROR,
      error
    })
  }
}

function* setNewPasswordSaga({ password }: I.ISetNewPasswordRequest) {
  try {
    yield put(startSubmit(newPasswordForm.name))
    const email = getResetEmail()
    const resetToken = getResetToken()

    if (!email || !resetToken) {
      yield put(push('/reset_password'))
      // eslint-disable-next-line
      throw { message: 'Your reset session has expired. Please start again' }
    }

    yield call(setNewPassword, email, password, resetToken)
    removeResetData()
    yield put({ type: t.SET_NEW_PASSWORD_SUCCESS })
    yield put(stopSubmit(newPasswordForm.name))
    yield put(setNotification('Password successfully reset'))
    yield put(push('/'))
  } catch (error) {
    yield put(stopSubmit(newPasswordForm.name))
    yield put({
      type: t.SET_NEW_PASSWORD_ERROR,
      error
    })
  }
}

function* logoutSaga() {
  removeSessionData()
  yield put({ type: t.LOGOUT_SUCCESS })
  yield put(push('/'))
}

function* routeChangeSaga() {
  while (true) {
    yield take(
      (action: LocationChangeAction) =>
        action.type === LOCATION_CHANGE &&
        action.payload.pathname.startsWith('/dashboard')
    )
    yield put(destroy(loginForm.name))
    yield put(destroy(signupForm.name))
  }
}

function* watchLoginSaga() {
  yield takeLatest(t.LOGIN_REQUEST, loginSaga)
}

function* watchSignupSaga() {
  yield takeLatest(t.SIGNUP_REQUEST, signupSaga)
}

function* watchLogoutSaga() {
  yield takeLatest(t.LOGOUT_REQUEST, logoutSaga)
}

function* watchPasswordResetSaga() {
  yield takeLatest(t.PASSWORD_RESET_REQUEST, passwordResetSaga)
}

function* watchVerifyOtpdSaga() {
  yield takeLatest(t.VERIFY_OTP_REQUEST, verifyOtpdSaga)
}

function* watchSetNewPasswordSaga() {
  yield takeLatest(t.SET_NEW_PASSWORD_REQUEST, setNewPasswordSaga)
}

function* watchGetCommisionSaga() {
  yield takeLatest(t.GET_COMMISSION_REQUEST, getCommisionSaga)
}

export default function*() {
  yield all([
    fork(watchLoginSaga),
    fork(watchSignupSaga),
    fork(watchLogoutSaga),
    fork(watchPasswordResetSaga),
    fork(watchVerifyOtpdSaga),
    fork(watchSetNewPasswordSaga),
    fork(watchGetCommisionSaga)
  ])

  // this should be at the end to prevent blocking other sagas
  yield fork(routeChangeSaga)
}
